/* eslint-disable no-nested-ternary */
import React, { useContext, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SuccessBanner from './SuccessBanner/SuccessBanner';
import CreatorComponent from '../Content/CreatorComponent/CreatorComponent';
import { getFormattedTimestamp } from '../Utilities/TimeFunctions';
import {
    CommunityContext,
    PageContext,
    GroupContext,
} from '../../contexts/ContextCreator';
import { constructCn, isEmpty } from '../../utilities/helperFunctions';
import ContentCard from './ContentCard';
import Pixel, {
    EmitMetric,
} from '../Analytics/VisibilityPixel/VisibilityPixel';
import CommentList from '../Community/CommentBox/CommentList/CommentList';
import CommentBox from '../Community/CommentBox/CommentBox';
import ModerationMenu from '../Community/ModerationMenu/ModerationMenu';

import './UnifiedPostCard.scss';
import ReactionBar from '../Community/ReactionBar/ReactionBar';
import useGroupActions from '../../hooks/useGroupActions';
import {
    emitEngagement,
    emitImpression,
    objectTypes,
} from '../../utilities/getStreamAnalytics';
import PostBoxContainer from '../UnifiedPostBox/PostBoxContainer/PostBoxContainer';

const UnifiedPostCard = ({
    className,
    contentUrl,
    isNewPost,
    time,
    topic,
    group,
    hash,
    streamDate,
    updatedAt,
    didEdit,
    author,
    location,
    showAuthor,
    isSingleContent,
    singleContentReactionBar,
    feedType,
    showComments,
    showReactionBar,
    ...props
}) => {
    const communityCtx = useContext(CommunityContext);
    const pageContext = useContext(PageContext);
    const groupContext = useContext(GroupContext);
    const { additionalRoles } = pageContext.pageProps.session;
    const [showingEditBox, setShowingEditBox] = useState(false);
    const [snowPlowInfo, setSnowPlowInfo] = useState({});
    const { isMember, requestToJoin } = useGroupActions(group, additionalRoles);
    const submitButtonText =
        !group || group.privacySetting === 'closed'
            ? 'Post'
            : isMember()
            ? 'Post'
            : 'Post & join';
    const bannerType = isNewPost ? 'new' : 'none';
    const isMasterFeed = useCallback(() => {
        return feedType === 'masterFeed';
    }, [feedType]);

    useEffect(() => {
        if (typeof communityCtx.pageProps !== 'undefined') {
            const { session } = communityCtx.pageProps;
            const { id, name } = session;
            emitImpression(
                { id, name },
                {
                    foreignId: `${objectTypes()[props.type]}:${hash}`,
                    actor: { id: `id: user:${id}` },
                    verb: 'viewContent',
                    object: { id: `${props.type}:${hash}` },
                }
            );
        }
        const baseSnowPlow = {
            component: 'tile',
            componentTitleType: 'article',
            position: 'main',
            pageType: 'collection',
            targetPageSection: 'advice',
            pageSection: 'advice_landing',
            companyId: props.article?.companyId,
            tileEntityId: `${props.type}-${hash}`,
            tileType: 'article',
            tileOrder: props.index + 1,
        };
        setSnowPlowInfo(baseSnowPlow);
    }, []);

    const handleLinkClickMetricEmit = (metricType) => {
        EmitMetric({
            misc_event_type: metricType,
            click: 1,
            snowPlow: {
                component: 'tile',
                componentTitleType: 'article',
                position: 'main',
                pageType: 'collection',
                targetPageSection: 'advice',
                pageSection: 'advice_landing',
                companyId: props.article?.companyId,
                tileEntityId: `${props.type}-${hash}`,
                tileOrder: props.index + 1,
            },
            community_content_key: `${props.type}-${hash}`,
            community_content_location: location,
        });
    };

    const onCommentBoxSubmit = async (groupHash, comment, isAnon) => {
        const isGroupMember =
            group && !isMember() && group.privacySetting === 'open';
        let groupResult = null;
        let userRelationResult = null;
        if (isGroupMember) {
            const response = await requestToJoin();
            if (response.meta.success) {
                const {
                    group: savedToGroup,
                    userRelation: resultantUserRelation,
                } = response;
                groupResult = savedToGroup;
                userRelationResult = resultantUserRelation;
                if (!isEmpty(groupContext)) {
                    groupContext.setGroup(savedToGroup);
                    groupContext.setUserRelation(resultantUserRelation);
                }
                pageContext.addToUserFullGroupList(savedToGroup);
            }
        }
        const commentResult = await communityCtx.submitComment(
            groupHash,
            comment,
            isAnon
        );
        if (commentResult?.success === false) {
            return commentResult;
        }
        if (isGroupMember) {
            communityCtx.setContentGroup({
                ...groupResult,
                userRelation: userRelationResult,
            });
        }
        return commentResult;
    };

    const renderComments = () => {
        const content = communityCtx.getContent(hash);

        if (!isMasterFeed() || content?.comments?.length < 1) return null;

        return (
            <CommentList
                location={location}
                hash={hash}
                handleLinkClickMetricEmit={handleLinkClickMetricEmit}
                handlePostAndJoin={onCommentBoxSubmit}
                submitButtonText={submitButtonText}
            />
        );
    };

    const renderCommentInput = () => {
        if (!isMasterFeed()) return null;
        const content = communityCtx.getContent(hash);
        const { session } = communityCtx.pageProps;
        const { username, logo } = session;
        const authorImage = {
            src: logo || '/v1/avatar_black_160_vtg7lw.svg',
            alt: `${username || 'default'} img`,
        };
        const isVip = !!communityCtx.userCommunityData?.isVip;

        const placeholderText =
            content.comments.length >= 1
                ? 'Join the conversation...'
                : 'Be the first to comment...';

        const showCommentBox = communityCtx.openedCommentHash === hash;

        return (
            <div id="unified-post-card-comment-input">
                <CommentBox
                    key={hash}
                    parentHash={hash}
                    onSubmit={onCommentBoxSubmit}
                    authorImage={authorImage}
                    isVip={isVip}
                    placeholderText={placeholderText}
                    showExpanded={showCommentBox}
                    submitButtonText={submitButtonText}
                />
            </div>
        );
    };

    const renderReactionBar = () => {
        if (isSingleContent && singleContentReactionBar)
            return singleContentReactionBar;
        const redirectToSinglePage =
            location === 'contributions' || location === 'resources-content';

        return (
            <ReactionBar
                hash={hash}
                redirectToPage={redirectToSinglePage}
                location={location}
                snowPlow={snowPlowInfo}
                className="left-aligned-bar"
            />
        );
    };

    const pixelMetrics = useCallback(() => {
        if (location === 'miniFeed') return;
        const { type, article, index } = props;
        const metrics = {
            community_content_key: `${type}-${hash}`,
            community_content_location: location,
            impression: 1,
            snowPlow: {
                component: 'tile',
                tileType: 'article',
                position: 'main',
                pageType: 'collection',
                pageSection: 'advice_landing',
                companyId: article?.companyId,
                tileEntityId: `${type}-${hash}`,
                tileOrder: index + 1,
            },
        };

        if (group) {
            metrics.community_group_hash = group.hash;
        }
        return metrics;
    }, [location, props, hash, group]);

    const handleOnEditClick = () => {
        setShowingEditBox(true);
    };

    const handleOnEditCanceled = () => {
        setShowingEditBox(false);
    };

    const handleOnClickMetricEmit = () => {
        if (!contentUrl) return null;

        EmitMetric({
            community_content_key: `${props.type}-${hash}`,
            community_content_location: location,
            click: 1,
            snowPlow: {
                component: 'tile',
                componentTitleType: 'article',
                position: 'main',
                pageType: 'collection',
                targetPageSection: 'advice',
                pageSection: 'advice_landing',
                companyId: props.article?.companyId,
                tileType: 'article',
                tileEntityId: `${props.type}-${hash}`,
                tileOrder: props.index + 1,
            },
            ...(group ? { community_group_hash: group.hash } : {}),
        });

        if (['editorial-content', 'community-podcast'].includes(props.type)) {
            const { session } = communityCtx.pageProps;
            const { id, name } = session;
            emitEngagement(
                { id, name },
                {
                    label: 'articleClick',
                    score: 1,
                    content: {
                        foreign_id: `${objectTypes()[props.type]}:${hash}`,
                    },
                }
            );
        }
    };

    // hide moderation menu for regular users seeing articles/ podcasts
    const userEditorialView =
        !pageContext?.isAdmin &&
        (props.type === 'editorial-content' ||
            props.type === 'community-podcast');
    const hideModerationMenu = props.hideModerationMenu || userEditorialView;
    const edited = didEdit === 'edit';
    const dateModified = edited ? updatedAt : streamDate;
    const redirectContent =
        props.type === 'community-podcast' ||
        props.type === 'editorial-content' ||
        location === 'homePage' ||
        location === 'searchResults';

    const cardCn = constructCn(
        'component-UnifiedPostCard',
        !isSingleContent && 'cardBorder',
        className,
        props.link ? 'community-link-styling' : 'community-discussion-styling'
    );

    return (
        <div className={cardCn}>
            <SuccessBanner type={bannerType} />
            <div className="cardContainer">
                <div className="creatorContainer">
                    {showAuthor && (
                        <CreatorComponent
                            author={author}
                            time={time}
                            topic={topic}
                            group={group}
                            edited={edited}
                            contentHash={hash}
                            className="postcard-creator-component"
                            formattedTimestamp={
                                dateModified
                                    ? getFormattedTimestamp(dateModified)
                                    : ''
                            }
                        />
                    )}
                    {!hideModerationMenu && (
                        <ModerationMenu
                            hash={hash}
                            location={location}
                            handleOnEditClick={handleOnEditClick}
                            editboxShowing={showingEditBox}
                        />
                    )}
                </div>
                <Pixel metrics={pixelMetrics()} />
                <div className="link-div" role="link" tabIndex={0}>
                    {showingEditBox ? (
                        <PostBoxContainer
                            {...props}
                            editMode={showingEditBox}
                            handleOnEditCanceled={handleOnEditCanceled}
                            hash={hash}
                        >
                            {' '}
                        </PostBoxContainer>
                    ) : (
                        [
                            redirectContent ? (
                                <a
                                    href={
                                        group?.groupUrl
                                            ? `${group.groupUrl}${contentUrl}`
                                            : contentUrl
                                    }
                                    onClick={handleOnClickMetricEmit}
                                    key={hash}
                                >
                                    <ContentCard
                                        {...props}
                                        location={location}
                                        type={props.type}
                                        hash={hash}
                                        iButtonPanelsSingleContent={
                                            isSingleContent
                                        }
                                        key={hash}
                                    />
                                </a>
                            ) : (
                                <ContentCard
                                    {...props}
                                    location={location}
                                    type={props.type}
                                    hash={hash}
                                    isSingleContent={isSingleContent}
                                    onClick={handleOnClickMetricEmit}
                                    key={hash}
                                />
                            ),
                        ]
                    )}
                </div>
                {showReactionBar && renderReactionBar()}
                {showComments && renderCommentInput()}
                {showComments ? (
                    isSingleContent ? (
                        <div className="comments-section">
                            {renderComments()}
                        </div>
                    ) : (
                        renderComments()
                    )
                ) : null}
            </div>
        </div>
    );
};

UnifiedPostCard.propTypes = {
    className: PropTypes.string,
    index: PropTypes.number,
    feedType: PropTypes.string,
    bannerType: PropTypes.oneOf(['new', 'edit', 'none']),
    isAnon: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    type: PropTypes.string.isRequired,
    streamDate: PropTypes.string,
    updatedAt: PropTypes.string,
    didEdit: PropTypes.string,
    author: PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string.isRequired,
        hash: PropTypes.string,
        image: PropTypes.shape({
            src: PropTypes.string.isRequired,
            alt: PropTypes.string,
        }).isRequired,
        userCommunityData: PropTypes.shape({
            id: PropTypes.number,
            connectionCount: PropTypes.number,
            followerCount: PropTypes.number,
            isVip: PropTypes.number,
        }),
        connectWithMeText: PropTypes.string,
        credential: PropTypes.string,
    }),
    canComment: PropTypes.bool.isRequired,
    commentCount: PropTypes.number.isRequired,
    comments: PropTypes.arrayOf(PropTypes.object).isRequired,
    showComments: PropTypes.bool,
    group: PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        groupUrl: PropTypes.string.isRequired,
        hash: PropTypes.string.isRequired,
        logo: PropTypes.shape({
            src: PropTypes.string.isRequired,
            alt: PropTypes.string.isRequired,
            name: PropTypes.string,
        }).isRequired,
        privacySetting: PropTypes.string.isRequired,
    }),
    moderationOption: PropTypes.shape({
        status: PropTypes.shape({
            lastEditDate: PropTypes.string,
            source: PropTypes.string,
            type: PropTypes.string,
            userId: PropTypes.number,
        }),
        editableFields: PropTypes.arrayOf(PropTypes.string),
    }),
    hideModerationMenu: PropTypes.bool,
    showReactionBar: PropTypes.bool,
    reactionBar: PropTypes.shape({
        commenting: PropTypes.shape({
            active: PropTypes.bool.isRequired,
            enabled: PropTypes.bool.isRequired,
            icon: PropTypes.string,
            label: PropTypes.string,
            showCount: PropTypes.bool,
        }),
        liking: PropTypes.shape({
            active: PropTypes.bool.isRequired,
            enabled: PropTypes.bool.isRequired,
            icon: PropTypes.string,
            label: PropTypes.string,
            showCount: PropTypes.bool,
        }),
        flagging: PropTypes.shape({
            active: PropTypes.bool.isRequired,
            enabled: PropTypes.bool.isRequired,
            icon: PropTypes.string,
            label: PropTypes.string,
            showCount: PropTypes.bool,
        }),
        sharing: PropTypes.shape({
            active: PropTypes.bool.isRequired,
            enabled: PropTypes.bool.isRequired,
            icon: PropTypes.string,
            label: PropTypes.string,
            showCount: PropTypes.bool,
        }),
    }).isRequired,
    time: PropTypes.string.isRequired,
    isNewPost: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
    }),
    link: PropTypes.shape({
        previewTitle: PropTypes.string,
        previewDescription: PropTypes.string,
        previewImage: PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
        }),
        embedHtml: PropTypes.string,
        externalUrl: PropTypes.string,
    }),
    article: PropTypes.shape({
        wink: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        sponsoredCompanyId: PropTypes.number,
        companyId: PropTypes.number,
        image: PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
        }),
    }),
    podcast: PropTypes.shape({
        wink: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
        }),
        audioSrc: PropTypes.string,
    }),
    rawHtml: PropTypes.string,
    hash: PropTypes.string.isRequired,
    contentUrl: PropTypes.string.isRequired,
    topic: PropTypes.shape({
        id: PropTypes.number,
        isActive: PropTypes.number,
        label: PropTypes.string,
        url: PropTypes.string,
    }),
    location: PropTypes.string,
    showAuthor: PropTypes.bool,
    isSingleContent: PropTypes.bool,
    singleContentReactionBar: PropTypes.shape({
        commenting: PropTypes.shape({
            active: PropTypes.bool.isRequired,
            enabled: PropTypes.bool.isRequired,
            icon: PropTypes.string,
            label: PropTypes.string,
            showCount: PropTypes.bool,
        }),
        liking: PropTypes.shape({
            active: PropTypes.bool.isRequired,
            enabled: PropTypes.bool.isRequired,
            icon: PropTypes.string,
            label: PropTypes.string,
            showCount: PropTypes.bool,
        }),
        flagging: PropTypes.shape({
            active: PropTypes.bool.isRequired,
            enabled: PropTypes.bool.isRequired,
            icon: PropTypes.string,
            label: PropTypes.string,
            showCount: PropTypes.bool,
        }),
        sharing: PropTypes.shape({
            active: PropTypes.bool.isRequired,
            enabled: PropTypes.bool.isRequired,
            icon: PropTypes.string,
            label: PropTypes.string,
            showCount: PropTypes.bool,
        }),
    }),
};

UnifiedPostCard.defaultProps = {
    className: '',
    feedType: '',
    index: 0,
    bannerType: 'none',
    location: '',
    author: {},
    group: {},
    streamDate: null,
    title: '',
    description: '',
    image: null,
    link: null,
    article: {},
    podcast: {},
    rawHtml: '',
    didEdit: null,
    updatedAt: '',
    topic: {},
    showAuthor: true,
    isSingleContent: false,
    moderationOption: {},
    singleContentReactionBar: {},
    showReactionBar: true,
    showComments: true,
    isAnon: false,
    isNewPost: false,
    hideModerationMenu: false,
};

UnifiedPostCard.displayName = 'UnifiedPostCard';

export default UnifiedPostCard;
